/**
 * PIQ Advocate Engine - Opus 4.5 Build
 * React Query hooks for fetching Advocate Engine data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './api-client'
import { QueryKeys, getInvalidators } from './query-cache'

// ============================================================================
// TypeScript Interfaces
// ============================================================================

// Mentions
export interface AdvocateMention {
  mention_id: string
  source_platform: string
  source_post_id: string
  author_handle: string
  author_display_name: string | null
  author_profile_url: string | null
  author_avatar_url: string | null
  author_follower_count: number | null
  author_verified: boolean
  raw_text: string
  sentiment: 'positive' | 'negative' | 'neutral' | 'mixed' | null
  sentiment_score: number | null
  intent: string | null
  intent_confidence: number | null
  topics: string[] | null
  brand_mentions: string[] | null
  engagement_metrics: Record<string, number>
  url: string | null
  published_at: string | null
  ingested_at: string
  processed_at: string | null
  advocate_id: string | null
  status: string
}

export interface MentionFilters {
  platform?: string
  sentiment?: string
  intent?: string
  status?: string
  date_from?: string
  date_to?: string
  search?: string
  has_advocate?: boolean
  page?: number
  limit?: number
}

export interface MentionsResponse {
  mentions: AdvocateMention[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
  filterOptions: {
    platforms: Array<{ value: string; count: number }>
    sentiments: Array<{ value: string; count: number }>
    intents: Array<{ value: string; count: number }>
    statuses: Array<{ value: string; count: number }>
  }
}

// Advocates
export interface Advocate {
  advocate_id: string
  primary_handle: string
  primary_platform: string
  display_name: string | null
  profile_url: string | null
  avatar_url: string | null
  bio: string | null
  follower_count: number
  handles: Array<{ platform: string; handle: string; verified?: boolean }>
  score: number
  tier: 'A' | 'B' | 'C' | null
  status: 'new' | 'invited' | 'active' | 'inactive' | 'declined'
  first_detected_at: string
  last_mention_at: string | null
  total_mentions: number
  positive_mentions: number
  total_revenue_generated: number
  total_commissions_paid: number
  activated_at: string | null
  tags: string[] | null
}

export interface AdvocateFilters {
  platform?: string
  tier?: string
  status?: string
  search?: string
  min_score?: number
  has_revenue?: boolean
  page?: number
  limit?: number
}

export interface AdvocatesResponse {
  advocates: Advocate[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
  summary: {
    tierCounts: Record<string, number>
    total: number
  }
}

// Scoring
export interface ScoreComponents {
  sentiment_strength: { value: number; max: number; raw: number; description: string }
  purchase_intent: { value: number; max: number; raw: number; description: string }
  engagement_velocity: { value: number; max: number; raw: number; description: string }
  audience_credibility: { value: number; max: number; tier: string; follower_count: number; description: string }
  category_relevance: { value: number; max: number; raw: number; description: string }
  recency_frequency: { value: number; max: number; mentions_30d: number; last_mention_days_ago: number; description: string }
}

export interface AdvocateScore {
  advocate_id: string
  score_total: number
  tier: 'A' | 'B' | 'C'
  components: ScoreComponents
  mentions_analyzed: number
}

// Offers
export interface AdvocateOffer {
  offer_id: string
  offer_name: string
  description: string | null
  commission_rate: number
  commission_type: 'percentage' | 'fixed'
  min_order_value: number | null
  max_commission: number | null
  max_monthly_commission: number | null
  bonus_rules: any[]
  return_window_days: number
  sku_exclusions: string[] | null
  category_exclusions: string[] | null
  min_tier: string | null
  active: boolean
  valid_from: string
  valid_until: string | null
  created_at: string
  active_links?: number
  advocate_count?: number
}

// Links
export interface AttributionLink {
  link_id: string
  advocate_id: string
  offer_id: string
  tracking_code: string
  destination_url: string
  utm_params: Record<string, string>
  clicks: number
  unique_clicks: number
  last_clicked_at: string | null
  active: boolean
  created_at: string
  expires_at: string | null
  primary_handle?: string
  primary_platform?: string
  offer_name?: string
  commission_rate?: number
}

// Conversions
export interface AdvocateConversion {
  conversion_id: string
  link_id: string
  order_id: string
  order_date: string
  gross_revenue: number
  discount_amount: number
  net_revenue: number | null
  refund_amount: number
  refunded: boolean
  return_window_ends: string
  return_window_complete: boolean
  tracking_code?: string
  primary_handle?: string
  offer_name?: string
  payout_status?: string
  final_commission?: number
}

// Payouts
export interface AdvocatePayout {
  payout_id: string
  advocate_id: string
  conversion_id: string
  gross_commission: number
  bonus_amount: number
  adjustment_amount: number
  final_commission: number
  commission_rate: number
  commission_type: string
  status: 'pending' | 'approved' | 'reduced' | 'paid' | 'cancelled' | 'failed'
  eligible_date: string
  approved_at: string | null
  paid_at: string | null
  payment_method: string | null
  payment_reference: string | null
  primary_handle?: string
  primary_platform?: string
  order_id?: string
  gross_revenue?: number
}

// Dashboard
export interface DashboardKPIs {
  total_advocates: number
  active_advocates: number
  tier_breakdown: { A: number; B: number; C: number }
  new_advocates_30d: number
  total_mentions: number
  mentions_30d: number
  positive_mentions_pct: number
  gross_revenue_attributed: number
  net_revenue_attributed: number
  total_refunds: number
  refund_rate: number
  total_commissions_paid: number
  pending_payout_liability: number
  avg_commission_rate: number
  roi: number
  cac_equivalent: number
  revenue_per_advocate: number
}

export interface TrendData {
  date: string
  revenue: number
  conversions: number
  commissions: number
  new_advocates: number
}

export interface TopAdvocate {
  advocate_id: string
  primary_handle: string
  primary_platform: string
  display_name: string | null
  tier: string
  score: number
  total_revenue: number
  total_conversions: number
  total_commissions: number
}

export interface DashboardResponse {
  kpis: DashboardKPIs
  trends: TrendData[]
  top_advocates: TopAdvocate[]
  platforms: Array<{
    platform: string
    advocate_count: number
    conversion_count: number
    revenue: number
  }>
  generated_at: string
}

// ============================================================================
// API BASE URL
// ============================================================================

const API_BASE = '/advocate-opus'

// ============================================================================
// QUERY HOOKS
// ============================================================================

// Dashboard
export function useAdvocateDashboard(days: number = 30) {
  return useQuery({
    queryKey: [...QueryKeys.advocateOpus.dashboard, days],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: DashboardResponse }>(
        `${API_BASE}/dashboard?days=${days}`
      )
      return response.data
    },
    staleTime: 60 * 1000, // 1 minute
  })
}

// Mentions
export function useMentions(filters: MentionFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.set(key, String(value))
    }
  })
  
  return useQuery({
    queryKey: [...QueryKeys.advocateOpus.mentions, filters],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MentionsResponse }>(
        `${API_BASE}/mentions?${params.toString()}`
      )
      return response.data
    },
    staleTime: 30 * 1000,
  })
}

// Advocates
export function useAdvocates(filters: AdvocateFilters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.set(key, String(value))
    }
  })
  
  return useQuery({
    queryKey: [...QueryKeys.advocateOpus.advocates, filters],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: AdvocatesResponse }>(
        `${API_BASE}/advocates?${params.toString()}`
      )
      return response.data
    },
    staleTime: 30 * 1000,
  })
}

export function useAdvocate(advocateId: string | null) {
  return useQuery({
    queryKey: QueryKeys.advocateOpus.advocate(advocateId || ''),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Advocate }>(
        `${API_BASE}/advocates?id=${advocateId}`
      )
      return response.data
    },
    enabled: !!advocateId,
    staleTime: 30 * 1000,
  })
}

// Advocate Score
export function useAdvocateScore(advocateId: string | null) {
  return useQuery({
    queryKey: [...QueryKeys.advocateOpus.advocate(advocateId || ''), 'score'],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: AdvocateScore }>(
        `${API_BASE}/scoring?advocate_id=${advocateId}`
      )
      return response.data
    },
    enabled: !!advocateId,
    staleTime: 60 * 1000,
  })
}

// Offers
export function useOffers(activeOnly: boolean = true) {
  return useQuery({
    queryKey: [...QueryKeys.advocateOpus.offers, { activeOnly }],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: { offers: AdvocateOffer[]; total: number } }>(
        `${API_BASE}/offers?active=${activeOnly}`
      )
      return response.data
    },
    staleTime: 60 * 1000,
  })
}

// Conversions
export function useConversions(filters: { advocate_id?: string; date_from?: string; date_to?: string; page?: number; limit?: number } = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined) {
      params.set(key, String(value))
    }
  })
  
  return useQuery({
    queryKey: [...QueryKeys.advocateOpus.conversions, filters],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: any }>(
        `${API_BASE}/conversions?${params.toString()}`
      )
      return response.data
    },
    staleTime: 30 * 1000,
  })
}

// Payouts
export function usePayouts(filters: { advocate_id?: string; status?: string; eligible_only?: boolean; page?: number; limit?: number } = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined) {
      params.set(key, String(value))
    }
  })
  
  return useQuery({
    queryKey: [...QueryKeys.advocateOpus.payouts, filters],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: any }>(
        `${API_BASE}/payouts?${params.toString()}`
      )
      return response.data
    },
    staleTime: 30 * 1000,
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

// Ingest mention
export function useIngestMention() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (payload: {
      source_platform: string
      source_post_id: string
      author_handle: string
      raw_text: string
      [key: string]: any
    }) => {
      const response = await apiClient.post<{ success: boolean; results: any }>(
        `${API_BASE}/mentions-ingest`,
        payload
      )
      return response
    },
    onSuccess: () => {
      const invalidators = getInvalidators(queryClient)
      invalidators.advocateOpus.afterIngest()
    },
  })
}

// Create advocate
export function useCreateAdvocate() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (payload: {
      primary_handle: string
      primary_platform: string
      display_name?: string
      [key: string]: any
    }) => {
      const response = await apiClient.post<{ success: boolean; data: Advocate }>(
        `${API_BASE}/advocates`,
        payload
      )
      return response
    },
    onSuccess: () => {
      const invalidators = getInvalidators(queryClient)
      invalidators.advocateOpus.afterIngest()
    },
  })
}

// Update advocate
export function useUpdateAdvocate() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ advocateId, ...payload }: { advocateId: string; [key: string]: any }) => {
      const response = await apiClient.put<{ success: boolean; data: Advocate }>(
        `${API_BASE}/advocates?id=${advocateId}`,
        payload
      )
      return response
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.advocate(variables.advocateId) })
      queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.advocates })
    },
  })
}

// Recalculate scores
export function useRecalculateScores() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (advocateId?: string) => {
      const url = advocateId 
        ? `${API_BASE}/scoring`
        : `${API_BASE}/scoring?action=recalculate-all`
      const response = await apiClient.post<{ success: boolean; data: any }>(
        url,
        advocateId ? { advocate_id: advocateId } : {}
      )
      return response
    },
    onSuccess: () => {
      const invalidators = getInvalidators(queryClient)
      invalidators.advocateOpus.afterScoring()
    },
  })
}

// Link mentions to advocates
export function useLinkMentions() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{ success: boolean; linked_count: number }>(
        `${API_BASE}/advocates?action=link-mentions`,
        {}
      )
      return response
    },
    onSuccess: () => {
      const invalidators = getInvalidators(queryClient)
      invalidators.advocateOpus.afterIngest()
    },
  })
}

// Create offer
export function useCreateOffer() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (payload: Partial<AdvocateOffer>) => {
      const response = await apiClient.post<{ success: boolean; data: AdvocateOffer }>(
        `${API_BASE}/offers`,
        payload
      )
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.offers })
    },
  })
}

// Update offer
export function useUpdateOffer() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ offerId, ...payload }: { offerId: string; [key: string]: any }) => {
      const response = await apiClient.put<{ success: boolean; data: AdvocateOffer }>(
        `${API_BASE}/offers?id=${offerId}`,
        payload
      )
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.offers })
    },
  })
}

// Create attribution link
export function useCreateLink() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (payload: {
      advocate_id: string
      offer_id: string
      destination_url: string
      utm_params?: Record<string, string>
    }) => {
      const response = await apiClient.post<{ success: boolean; data: AttributionLink & { full_tracking_url: string } }>(
        `${API_BASE}/attribution-links`,
        payload
      )
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.links })
      queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.advocates })
    },
  })
}

// Process eligible payouts
export function useProcessPayouts() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{ success: boolean; data: any }>(
        `${API_BASE}/payouts?action=process-eligible`,
        {}
      )
      return response
    },
    onSuccess: () => {
      const invalidators = getInvalidators(queryClient)
      invalidators.advocateOpus.afterPayout()
    },
  })
}

// Approve payouts
export function useApprovePayouts() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (payoutIds: string[]) => {
      const response = await apiClient.post<{ success: boolean; data: any }>(
        `${API_BASE}/payouts?action=approve`,
        { payout_ids: payoutIds }
      )
      return response
    },
    onSuccess: () => {
      const invalidators = getInvalidators(queryClient)
      invalidators.advocateOpus.afterPayout()
    },
  })
}

// Mark payouts as paid
export function useMarkPayoutsPaid() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (payload: {
      payout_ids: string[]
      payment_method: string
      payment_reference?: string
    }) => {
      const response = await apiClient.post<{ success: boolean; data: any }>(
        `${API_BASE}/payouts?action=mark-paid`,
        payload
      )
      return response
    },
    onSuccess: () => {
      const invalidators = getInvalidators(queryClient)
      invalidators.advocateOpus.afterPayout()
    },
  })
}
